import { UserRole } from '../utils/role-utils';

export interface NavigationItem {
  path?: string;
  label: string;
  roles: UserRole[];
  /** When true, navigate with a full page load (e.g. static HTML outside the SPA). */
  external?: boolean;
  /** Nested items (e.g. Help manuals). Filtered by role when building nav. */
  children?: NavigationItem[];
}

/**
 * Navigation configuration for each user role
 */
export const roleNavigation: Record<UserRole, NavigationItem[]> = {
  super_user: [
    {
      path: '/',
      label: 'Dashboard',
      roles: ['super_user'],
    },
    {
      path: '/sites',
      label: 'Sites',
      roles: ['super_user'],
    },
    {
      path: '/users',
      label: 'Users',
      roles: ['super_user'],
    },
    {
      path: '/organisation',
      label: 'Organisation',
      roles: ['super_user'],
    },
    {
      path: '/branding',
      label: 'Branding',
      roles: ['super_user'],
    },
    {
      path: '/usage',
      label: 'Usage',
      roles: ['super_user'],
    },
    {
      path: '/logs',
      label: 'Logs',
      roles: ['super_user'],
    },
    // {
    //   path: '/audit',
    //   label: 'Audit',
    //   roles: ['super_user'],
    // },
  ],
  site_admin: [
    {
      path: '/',
      label: 'Dashboard',
      roles: ['site_admin'],
    },
    {
      path: '/sites',
      label: 'My Sites',
      roles: ['site_admin'],
    },
    {
      path: '/users',
      label: 'Users',
      roles: ['site_admin'],
    },
    {
      path: '/usage',
      label: 'Usage',
      roles: ['site_admin'],
    },
  ],
  end_user: [
    {
      path: '/',
      label: 'Dashboard',
      roles: ['end_user'],
    },
    {
      path: '/usage',
      label: 'Usage',
      roles: ['end_user'],
    },
  ],
};

/**
 * Help manuals (SPA /help/:slug → authenticated GET /api/manuals/:file).
 * Keep roles in sync with cloud-backend/src/routes/manuals.routes.ts.
 */
const helpManualItems: NavigationItem[] = [
  {
    path: '/help/quick-start-end-users',
    label: 'Quick Start',
    roles: ['end_user', 'site_admin', 'super_user'],
  },
  {
    path: '/help/end-user-guide',
    label: 'End User Guide',
    roles: ['end_user', 'site_admin', 'super_user'],
  },
  {
    path: '/help/site-administrator-guide',
    label: 'Site Administrator Guide',
    roles: ['site_admin', 'super_user'],
  },
  {
    path: '/help/super-user-guide',
    label: 'Super User Guide',
    roles: ['super_user'],
  },
  {
    path: '/help/zerotier-setup',
    label: 'ZeroTier Setup',
    roles: ['super_user'],
  },
];

/**
 * Get navigation items for a specific role, including a Help submenu filtered by role.
 */
export function getRoleNavigation(role: UserRole | undefined): NavigationItem[] {
  if (!role) return [];

  const helpChildren = helpManualItems.filter((item) => item.roles.includes(role));
  const helpItem: NavigationItem | null =
    helpChildren.length > 0
      ? {
          label: 'Help',
          roles: ['super_user', 'site_admin', 'end_user'],
          children: helpChildren,
        }
      : null;

  return [...(roleNavigation[role] || []), ...(helpItem ? [helpItem] : [])];
}

/**
 * Check if a navigation item should be visible for a role
 */
export function isNavigationItemVisible(item: NavigationItem, role: UserRole | undefined): boolean {
  if (!role) return false;
  return item.roles.includes(role);
}
